import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input } from '@arco-design/web-react';

import styles from './requiredField.module.less';
import type { VendorDeviceSelection } from './types';
import { createVendorDeviceSelectionRequiredRule, resolveVendorDeviceRequiredValidateField } from './validation';

interface VendorDeviceSelectionRequiredFieldProps {
  /** 虚拟校验字段前缀（如 resource.cpuPricing、pricing、bareMetalNodes） */
  fieldPrefix: string;
  /** 读取当前选中项 */
  readSelection: () => VendorDeviceSelection | null | undefined;
  /** 自定义错误文案 */
  message?: string;
  /**
   * 选择器等内容：包在 Form.Item 内，校验失败时错误展示在下方。
   * 勿对整个 Item 使用 hidden，否则看不到「请选择厂商设备」。
   */
  children?: ReactNode;
  /**
   * 校验提示上移贴列表（默认 true，适配 VendorAndDeviceRadioList）。
   * 输入框 / 双列表单场景传 false，避免与控件重叠。
   */
  compactMessage?: boolean;
}

/** 注册 Form 校验；children 为厂商设备选择器时，错误展示在其下方（间距对齐 Arco 表单项） */
export function VendorDeviceSelectionRequiredField({
  fieldPrefix,
  readSelection,
  message,
  children,
  compactMessage = true,
}: VendorDeviceSelectionRequiredFieldProps) {
  const { t } = useTranslation();
  const field = resolveVendorDeviceRequiredValidateField(fieldPrefix);
  const resolvedMessage = message ?? t('data-center:vendor.device.selection.required');
  const rules = useMemo(() => [createVendorDeviceSelectionRequiredRule(readSelection, resolvedMessage)], [readSelection, resolvedMessage]);

  return (
    <Form.Item
      field={field}
      rules={rules}
      className={compactMessage ? styles.requiredItemCompact : styles.requiredItem}
      style={{ marginBottom: children ? undefined : 0 }}
    >
      {/* 包一层避免 Form 向列表注入 value/onChange；无 children 时仍注册校验字段 */}
      {children ? <div>{children}</div> : <Input style={{ display: 'none' }} />}
    </Form.Item>
  );
}
